import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone';

import TextInput from '../TextInput';
import StepContainer from '../StepContainer';
import FixedFooter from '../Footer/FixedFooter';
import AccountFeatures from '../AccountFeatures';

import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';
import { formatPhoneNumber } from '@evdy/web-core/dist/lib/utils';
import { usePageWidthListener } from '@evdy/web-core/dist/customHooks';

import styles from './CreateAccount.module.scss';

const { updateUserPackageInfo, saveLastScreen, resetPlanPackageUpdated } = authUserOperations;

const {
  'phone-input': phoneInputContainer,
  'custom-footer': customFooterClass,
  'custom-footer-content': customFooterContentClass,
  skip: skipBtnClass
} = styles;

const CreateAccount = () => {
  const {
    authUser,
    pageSize: { width }
  } = useSelector(({ authUser, pageSize }) => ({
    authUser,
    pageSize
  }));
  const { data: authUserData, isFetching, planPackageUpdated } = authUser ?? {};
  const dispatch = useDispatch();

  const userPhoneSchema = Yup.object().shape({
    phone: Yup.string()
      .phone('US', true, 'Please enter a valid phone number.')
      .required('Phone is required')
  });

  const handleSubmit = ({ phone }) => {
    const strippedPhone = phone?.replace(/\D+/g, '');
    const userInfo = {
      ...authUserData.preneed.userInfo,
      phone: strippedPhone
    };
    dispatch(updateUserPackageInfo({ userInfo }));
  };

  const renderAdditionalContent = () => (
    <button
      className={skipBtnClass}
      onClick={() => dispatch(saveLastScreen({ screen: 'completeDetails' }))}
    >
      {width > 768 ? `Skip for now` : 'Skip'}
    </button>
  );

  const backAction = () => dispatch(saveLastScreen({ screen: 'chooseProvider' }));

  //move to completeDetails on success
  useEffect(() => {
    if (planPackageUpdated) {
      dispatch(saveLastScreen({ screen: 'completeDetails' }));
      dispatch(resetPlanPackageUpdated());
    }
  }, [planPackageUpdated]);

  usePageWidthListener();

  return (
    <Formik
      initialValues={{ phone: '' || formatPhoneNumber(authUserData?.preneed?.userInfo?.phone) }}
      validationSchema={userPhoneSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ isValid, values, handleSubmit }) => (
        <>
          <StepContainer
            evelynArray={[
              'The best way to save your plan is with a free Everdays account. We’ll set yours up using your phone number as your personal login.',
              { content: 'Add your number to create your free account.', style: 'header' }
            ]}
            showLoader={isFetching}
          >
            <Form>
              <div className={phoneInputContainer}>
                <TextInput
                  type="tel"
                  label="Phone Number"
                  name="phone"
                  placeholder="(___) ___-____"
                />
              </div>
            </Form>
            <AccountFeatures />
          </StepContainer>
          <FixedFooter
            {...{ isValid, renderAdditionalContent, backAction }}
            customClass={customFooterClass}
            mainContentCustomClass={customFooterContentClass}
            nextCopy="Next"
            nextAction={handleSubmit}
            backCopy="Back"
          />
        </>
      )}
    </Formik>
  );
};

export default CreateAccount;
